import React, { useState } from "react";
import { Button } from "react-bootstrap";
import SidebarSection from "./SidebarSection";

interface EnrollmentSidebarProps {
  details: any;
}

const SidebarRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="ev-sidebar-row">
    <span className="ev-sidebar-label">{label}</span>
    {/* Guard against `value || "--"` — that would hide a legitimate 0
        (e.g. a Grade Point of 0). Only null/undefined/"" are "empty". */}
    <span className="ev-sidebar-value">
      {value == null || value === "" ? "--" : value}
    </span>
  </div>
);

const copyToClipboard = (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return Promise.reject(new Error("Clipboard unavailable"));
};

const CopyIcon: React.FC<{ value?: string; title?: string }> = ({ value, title }) => {
  const [done, setDone] = useState(false);
  if (!value) return null;
  const doCopy = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    copyToClipboard(value)
      .then(() => {
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      })
      .catch(() => {
        /* insecure context / permission denied — no crash */
      });
  };
  return (
    <i
      className={`fa ${done ? "fa-check" : "fa-copy"} ev-copy-icon`}
      title={title || "Copy"}
      role="button"
      tabIndex={0}
      aria-label={title || "Copy"}
      onClick={doCopy}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          doCopy(e);
        }
      }}
    />
  );
};

const EnrollmentSidebar: React.FC<EnrollmentSidebarProps> = ({ details }) => {
  const [copied, setCopied] = useState(false);

  if (!details) return null;
  const d = details;
  const dn = d.displayNames || {};

  const handleCopy = () => {
    const text = [
      `Name: ${d.studentName || ""}`,
      `Student Code: ${d.studentCode || ""}`,
      `Enquiry ID: ${d.enquiryId || ""}`,
      `Username: ${d.studentUserName || ""}`,
      `SSO Id: ${d.ssoId || ""}`,
      `${dn.batch || "Batch"}: ${d.batchName || ""}`,
      `${dn.course || dn.program || "Course"}: ${d.courseName || d.programName || ""}`,
      `Status: ${d.enrollmentStatusName || ""}`,
    ].join("\n");
    copyToClipboard(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        /* insecure context / permission denied — no crash */
      });
  };

  return (
    <div className="ev-sidebar">
      {/* Student Identity */}
      <div className="ev-sidebar-section">
        <div className="ev-identity">
          {d.photoPath ? (
            <img
              src={d.photoPath}
              alt=""
              className="ev-photo"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
                (e.target as HTMLImageElement).nextElementSibling?.removeAttribute("hidden");
              }}
            />
          ) : null}
          <div className="ev-photo-placeholder" hidden={!!d.photoPath}>
            <i className="fa fa-user" />
          </div>
          <div className="ev-identity-info">
            <div className="ev-identity-name">
              {d.studentName}
              <CopyIcon value={d.studentName} title="Copy name" />
            </div>
            <div className="ev-identity-code">
              {d.studentCode && (
                <span className="ev-student-code-highlight">{d.studentCode}</span>
              )}
              {d.studentCode && (
                <CopyIcon value={d.studentCode} title="Copy student code" />
              )}
            </div>
            {d.enrollmentStatusName && (
              <span
                className="ev-status-badge"
                style={{
                  background: d.statusColor || "var(--status-success-bg)",
                  color: d.statusColor ? "var(--text-on-brand)" : "var(--status-success)",
                  marginTop: 4,
                }}
              >
                {d.enrollmentStatusName}
              </span>
            )}
          </div>
        </div>
        <div className="ev-sidebar-section-body">
          <SidebarRow label="Enquiry ID" value={d.enquiryId} />
          <SidebarRow label="Student Username" value={d.studentUserName} />
          {d.showSsoId && <SidebarRow label="SSO Id" value={d.ssoId} />}
          <SidebarRow label="Parent Username" value={d.parentUserName} />
          <SidebarRow label="Counsellor" value={d.counsellor} />
          <div style={{ marginTop: 6 }}>
            <Button
              variant="secondary"
              size="sm"
              style={{ fontSize: 11, padding: "2px 10px" }}
              onClick={handleCopy}
            >
              <i className="fa fa-copy" /> {copied ? "Copied!" : "Copy Info"}
            </Button>
          </div>
        </div>
      </div>

      {/* Academic Timeline — Term & Graduation are program-based concepts */}
      <SidebarSection title="Academic Timeline">
        <SidebarRow label="Join Date" value={d.joiningDate} />
        <SidebarRow label="Entry Year" value={d.entryYearName} />
        <SidebarRow label="Year" value={d.batchYearName} />
        {d.isProgramBased && (
          <>
            <SidebarRow label="Entry Term" value={d.entryTermName} />
            <SidebarRow label="Current Term" value={d.currentTermName} />
            <SidebarRow label="Term" value={d.batchTermName} />
            {d.graduationDate && (
              <SidebarRow label="Graduation Date" value={d.graduationDate} />
            )}
          </>
        )}
        <SidebarRow label={dn.batch || "Batch"} value={d.batchName} />
      </SidebarSection>

      {/* Course / Program Details */}
      <SidebarSection title={d.isProgramBased ? `${dn.program || "Program"} Details` : `${dn.course || "Course"} Details`}>
        <SidebarRow label={dn.school || "School / Centre"} value={d.schoolName} />
        <SidebarRow label="Plan Name" value={d.installmentTemplateName} />
        {d.isProgramBased ? (
          <SidebarRow label={dn.program || "Program"} value={d.programName} />
        ) : (
          <>
            {d.courseTypeName && (
              <SidebarRow label={`${dn.course || "Course"} Type`} value={d.courseTypeName} />
            )}
            <SidebarRow label={dn.course || "Course"} value={d.courseName} />
          </>
        )}
        <SidebarRow label="Faculty Advisor" value={d.facultyAdvisorNames} />
      </SidebarSection>

      {/* Metadata */}
      <SidebarSection title="Metadata">
        <SidebarRow label="Promo Code" value={d.promoCode} />
        <SidebarRow label="Remarks" value={d.remarks} />
        <SidebarRow label="Groups" value={d.subBatchNames} />
        {d.tags && d.tags.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <div style={{ fontWeight: 600, fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Tags</div>
            {d.tags.map((tag: any, idx: number) => (
              <div key={idx} style={{ fontSize: 11, borderBottom: "1px solid var(--border-subtle)", padding: "2px 0" }}>
                <span style={{ color: "var(--text-muted)" }}>{tag.typeName}: </span>
                <span style={{ fontWeight: 500 }}>{tag.tagName}</span>
                {tag.taggedDate && (
                  <span style={{ fontSize: 10, color: "var(--text-faint)", marginLeft: 4 }}>
                    ({tag.taggedDate})
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </SidebarSection>

      {/* Status & Attributes */}
      <SidebarSection title="Status & Attributes" defaultOpen={false}>
        {d.enrollmentCategory && (
          <SidebarRow label="Category" value={d.enrollmentCategory} />
        )}
        {d.enrollmentCategory2 && (
          <SidebarRow label="Category 2" value={d.enrollmentCategory2} />
        )}
        {d.enrollmentQuota && (
          <SidebarRow label="Quota" value={d.enrollmentQuota} />
        )}
        <SidebarRow label="Lateral Entry" value={d.lateralEntry ? "Yes" : "No"} />
        {!d.isProgramBased && d.graduationDate && (
          <SidebarRow label="Graduation Date" value={d.graduationDate} />
        )}
        {d.gradePoint != null && (
          <SidebarRow label="Grade Point" value={d.gradePoint} />
        )}
      </SidebarSection>
    </div>
  );
};

export default EnrollmentSidebar;
